import Hammer from 'hammerjs';
import { Path, Point, HitResultType } from './core';
import PaintStack, { StackPattern } from './paint_stack';
export var MODE_TOUCH;
(function (MODE_TOUCH) {
    MODE_TOUCH[MODE_TOUCH["pan"] = 0] = "pan";
    MODE_TOUCH[MODE_TOUCH["move"] = 1] = "move";
})(MODE_TOUCH || (MODE_TOUCH = {}));
;
export default class {
    constructor(canvas, image, { debug = false, removeOffset = 60, hitOffset = 30, lineWidth = 15, lineOutWidth = 20, lineColor = 'rgba(255, 0, 0, 1)', lineOutColor = 'rgba(0, 255, 0, 1)', closedLineWidth = 15, closedLineOutWidth = 20, closedLineColor = 'rgba(255, 0, 0, 1)', closedLineOutColor = 'rgba(0, 255, 0, 1)', endpointColor = 'rgba(0, 0, 0, 0.6)', endpointRadius = 30, maxScale = 6, minScale = 1, onStackChange = (stack) => { } } = {}) {
        this._offsetX = (ev) => {
            const offset = ev.target.getBoundingClientRect();
            return ev.center.x - offset.left;
        };
        this._offsetY = (ev) => {
            const offset = ev.target.getBoundingClientRect();
            return ev.center.y - offset.top;
        };
        this._canvas = canvas;
        this._context = canvas.getContext('2d');
        this._svg = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
        this._matrix = this._svg.createSVGMatrix();
        this._offset = new Point(this._canvas.width / 2, this._canvas.height / 2);
        this._image = image;
        this._mode = MODE_TOUCH.pan;
        this._onStackChange = onStackChange;
        this._paintStack = new PaintStack(this._onStackChange);
        // option init
        const box = this._canvas.getBoundingClientRect();
        const scale = this._canvas.width / box.width;
        this._debug = debug;
        this._removeOffset = removeOffset * scale;
        this._hitOffset = hitOffset * scale;
        this._lineWidth = lineWidth * scale;
        this._lineOutWidth = lineOutWidth * scale;
        this._lineColor = lineColor;
        this._lineOutColor = lineOutColor;
        this._closedLineWidth = closedLineWidth * scale;
        this._closedLineOutWidth = closedLineOutWidth * scale;
        this._closedLineColor = closedLineColor;
        this._closedLineOutColor = closedLineOutColor;
        this._endpointRadius = endpointRadius * scale;
        this._endpointColor = endpointColor;
        this._maxScale = maxScale;
        this._minScale = minScale;
        // 初始化手势
        this._scaleValue = 1;
        const _hammer = new Hammer(canvas);
        _hammer.get('pinch').set({ enable: true });
        _hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        // 拖动开始
        _hammer.on('panstart', this._panstart.bind(this));
        // 拖动中
        _hammer.on('panmove', this._panmove.bind(this));
        // 拖动结束
        _hammer.on('panend', this._panend.bind(this));
        // 开始缩放
        _hammer.on('pinchstart', this._pinchstart.bind(this));
        // 缩放中
        _hammer.on('pinchmove', this._pinchmove.bind(this));
        // 绘制
        this._paint();
    }
    // 变换模式
    modeToggle() {
        this._mode = this._mode === MODE_TOUCH.pan ? MODE_TOUCH.move : MODE_TOUCH.pan;
    }
    /** 切换到画线模式 */
    togglePan() {
        this._mode = MODE_TOUCH.pan;
    }
    /** 切换到手势移动模式 */
    toggleMove() {
        this._mode = MODE_TOUCH.move;
    }
    // 生成
    async toClipURL() {
        const _width = this._canvas.width;
        const _height = this._canvas.height;
        this._clear();
        if (this._paintStack.getLast() === undefined || this._paintStack.pattern == StackPattern.pan) {
            let _width = this._canvas.width;
            let _height = this._image.height * _width / this._image.width;
            this._canvas.width = _width;
            this._canvas.height = _height;
            this._context && this._context.drawImage(this._image, 0, 0, _width, _height);
        }
        else {
            const path = this._paintStack.getLast();
            if (path && this._context) {
                this._context.setTransform(1, 0, 0, 1, 0, 0);
                const bound = path.getBounds();
                path.drawPath(this._context);
                this._context.clip();
                this._paintImage();
                const source = this._canvas.toDataURL();
                this._clear();
                const image = await this._loadImage(source);
                this._canvas.width = bound[2];
                this._canvas.height = bound[3];
                this._context.drawImage(image, bound[0], bound[1], bound[2], bound[3], 0, 0, bound[2], bound[3]);
            }
        }
        const base64 = this._canvas.toDataURL();
        this._canvas.width = _width;
        this._canvas.height = _height;
        // 重置 paint stack
        this._paintStack = new PaintStack(this._onStackChange);
        return base64;
    }
    /** 销毁 */
    dispose() {
        this.clear();
        this._canvas.width = 0;
        this._canvas.height = 0;
    }
    // 撤销
    revoke() {
        this._paintStack.revoke();
        this._paint();
    }
    // 放大
    zoomIn(scale = 1.1) {
        this._offset.x = this._canvas.clientWidth / 2;
        this._offset.y = this._canvas.clientHeight / 2;
        this._zoom(scale);
    }
    // 缩小
    zoomOut(scale = 0.9) {
        this._offset.x = this._canvas.clientWidth / 2;
        this._offset.y = this._canvas.clientHeight / 2;
        this._zoom(scale);
    }
    // 清空canvas
    clear() {
        this._clear();
        this._paintStack = new PaintStack(this._onStackChange);
    }
    _clear() {
        if (this._context) {
            this._context.save();
            this._context.setTransform(1, 0, 0, 1, 0, 0);
            this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);
            this._context.restore();
        }
    }
    // 更换图片
    updateImage(image) {
        this._image = image;
        this.clear();
        this._paintImage();
    }
    _paint() {
        this._clear();
        this._paintImage();
        this._paintPathing();
        this._paintPathed();
    }
    _paintImage() {
        let _width = this._canvas.width;
        let _height = this._image.height * _width / this._image.width;
        // 根据宽高比赋值渲染尺寸
        if (this._image.width / this._canvas.width < this._image.height / this._canvas.height) {
            _height = this._canvas.height;
            _width = this._image.width * _height / this._image.height;
        }
        this._context && this._context.drawImage(this._image, (this._canvas.width - _width) / 2, (this._canvas.height - _height) / 2, _width, _height);
    }
    _paintPathing() {
        if (this._pathing === undefined || this._pathing.segments.length == 0)
            return;
        this._paintStroke(this._pathing, this._lineWidth, this._lineColor, this._lineOutWidth, this._lineOutColor);
        // 开始端点
        const first = this._pathing.segments[0].point;
        this._paintArc(first, this._endpointRadius, this._endpointColor);
        // 结束端点
        if (this._pathing.segments.length > 1) {
            const last = this._pathing.segments[this._pathing.segments.length - 1].point;
            this._paintArc(last, this._endpointRadius, this._endpointColor);
        }
    }
    _paintPathed() {
        if (this._paintStack.getLast() === undefined)
            return;
        const path = this._paintStack.getLast();
        if (path) {
            if (path.closed) { // 线段闭合
                // const bound = path.getBounds();
                // this._context.rect(bound[0], bound[1], bound[2], bound[3]);
                // this._context.strokeStyle = 'white';
                // this._context.stroke();
                this._paintStroke(path, this._closedLineWidth, this._closedLineColor, this._closedLineOutWidth, this._closedLineOutColor);
            }
            else {
                this._paintStroke(path, this._lineWidth, this._lineColor, this._lineOutWidth, this._lineOutColor);
                // 开始端点
                const first = path.segments[0].point;
                this._paintArc(first, this._endpointRadius, this._endpointColor);
                // 结束端点
                if (path.segments.length > 1) {
                    const last = path.segments[path.segments.length - 1].point;
                    this._paintArc(last, this._endpointRadius, this._endpointColor);
                }
            }
        }
    }
    _paintStroke(path, width, color, outWidth, outColor) {
        if (this._context) {
            // out
            this._context.lineWidth = outWidth;
            this._context.strokeStyle = outColor;
            this._context.lineJoin = 'round';
            this._context.lineCap = 'round';
            path.drawPath(this._context);
            this._context.stroke();
            // in
            if (outWidth > width || color != outColor) {
                this._context.lineWidth = width;
                this._context.strokeStyle = color;
                this._context.lineJoin = 'round';
                this._context.lineCap = 'round';
                path.drawPath(this._context);
                this._context.stroke();
            }
            if (this._debug) {
                path.segments.forEach((segment) => {
                    this._paintArc(segment.point, 10, '#fff');
                });
            }
        }
    }
    _paintArc(point, radius, color) {
        if (this._context) {
            this._context.beginPath();
            this._context.arc(point.x, point.y, radius, 0, 2 * Math.PI);
            this._context.fillStyle = color;
            this._context.fill();
        }
    }
    _hitTest(point) {
        const _hitPath = this._paintStack.getLast();
        const _hitResult = _hitPath && _hitPath.hitTest(point, this._hitOffset);
        if (_hitResult && _hitResult.type == HitResultType.segment) {
            this._hitSegment = _hitResult.segment;
            if (this._hitSegment && _hitPath) {
                // 删除范围内的点
                this._hitSegment.featured = true;
                _hitPath.removeNearSegment(this._hitSegment, this._removeOffset / 3);
            }
        }
        else if (_hitResult && _hitResult.type == HitResultType.stroke) {
            const _location = _hitResult.location;
            const _index = _location && _location.getIndex();
            const _point = _location && _location.point;
            if (_index !== undefined && _point && _hitPath) {
                const _prev = _hitPath.segments[_index];
                const _next = _hitPath.nextSegment(_prev);
                if (_prev.featured && point.isClose(_prev.point, this._removeOffset)) { // 判断上一个点是不是特征点
                    this._hitSegment = _prev;
                }
                else if (_next && _next.featured && point.isClose(_next.point, this._removeOffset)) { // 判断下一个点是不是特征点
                    this._hitSegment = _next;
                }
                else {
                    this._hitSegment = _hitPath.insert(_point, _index + 1);
                    if (this._hitSegment) {
                        this._hitSegment.featured = true;
                        const offset = _hitPath.getOffsetOf(this._hitSegment.point) || 0;
                        const length = _hitPath.getLength();
                        const point1 = _hitPath.getPointAt(offset + this._removeOffset);
                        const point2 = _hitPath.getPointAt(0 < offset - this._removeOffset ? offset - this._removeOffset : length - offset + this._removeOffset);
                        // 删除范围内的点
                        _hitPath.removeNearSegment(this._hitSegment, this._removeOffset);
                        // 插入范围内的点
                        if (point1) {
                            const next = _hitPath.insert(point1, this._hitSegment.index + 1);
                            next && _hitPath.removeNextSegment(next, this._removeOffset / 2);
                        }
                        if (point2) {
                            const prev = _hitPath.insert(point2, this._hitSegment.index);
                            prev && _hitPath.removePrevSegment(prev, this._removeOffset / 2);
                        }
                    }
                }
            }
        }
    }
    _panstart(ev) {
        if (this._mode == MODE_TOUCH.pan) {
            if (this._paintStack.pattern === StackPattern.pan)
                this._pathing = new Path();
            else if (this._paintStack.pattern === StackPattern.move) {
                const last = this._paintStack.getLast();
                last && this._paintStack.add(last.clone());
            }
        }
        else if (this._mode == MODE_TOUCH.move) {
            this._offset.x = this._offsetX(ev);
            this._offset.y = this._offsetY(ev);
            this._normalizedOffset = this._transformedPoint(this._offset);
        }
    }
    _panmove(ev) {
        this._offset.x = this._offsetX(ev);
        this._offset.y = this._offsetY(ev);
        const point = this._transformedPoint(this._offset);
        if (this._mode == MODE_TOUCH.pan) {
            if (this._paintStack.pattern === StackPattern.pan) {
                this._pathing && this._pathing.addPoint(point);
            }
            else if (this._paintStack.pattern === StackPattern.move) {
                if (this._hitSegment) {
                    this._hitSegment.point = point;
                    const last = this._paintStack.getLast();
                    last && last.smooth(this._hitSegment);
                }
                else {
                    this._hitTest(point);
                }
            }
            this._paint();
        }
        else if (this._mode == MODE_TOUCH.move && this._normalizedOffset) {
            this._translate(point.x - this._normalizedOffset.x, point.y - this._normalizedOffset.y);
            this._paint();
        }
    }
    _panend() {
        if (this._mode == MODE_TOUCH.pan) {
            if (this._paintStack.pattern === StackPattern.pan)
                this._pathing && this._paintStack.join(this._pathing, this._endpointRadius * 2);
            this._pathing = this._hitSegment = undefined;
            this._paint();
        }
        this._normalizedOffset = undefined;
    }
    _pinchstart(ev) {
        this._offset.x = this._offsetX(ev);
        this._offset.y = this._offsetY(ev);
    }
    _pinchmove(ev) {
        let scale = 1 + (ev.scale - 1) / 12;
        if (ev.scale < 1) {
            scale = 1 + (ev.scale - 1) / 6;
        }
        this._zoom(scale);
        ev.preventDefault();
    }
    _zoom(scale) {
        if (this._scaleValue * scale <= this._maxScale && this._scaleValue * scale >= this._minScale) {
            this._scaleValue *= scale;
            const point = this._transformedPoint(this._offset);
            this._translate(point.x, point.y);
            this._scale(scale);
            this._translate(-point.x, -point.y);
            this._paint();
        }
    }
    _transformedPoint(point) {
        let _point = this._svg.createSVGPoint();
        const box = this._canvas.getBoundingClientRect();
        const scale = this._canvas.width / box.width;
        _point.x = point.x * scale;
        _point.y = point.y * scale;
        _point = _point.matrixTransform(this._matrix.inverse());
        return new Point(_point.x, _point.y);
    }
    _translate(x, y) {
        this._matrix = this._matrix.translate(x, y);
        this._context && this._context.translate(x, y);
    }
    _scale(scale) {
        this._matrix = this._matrix.scaleNonUniform(scale, scale);
        this._context && this._context.scale(scale, scale);
    }
    _loadImage(data) {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = _ => resolve(image);
            image.onerror = _ => reject(image);
            image.src = data;
        });
    }
}
//# sourceMappingURL=index.js.map